import { Circle } from 'lucide-react';

/* Whether the handset is registered, and where from.

   Status is live from the PBX's manager interface, not configuration — a
   phone that's unplugged is offline whatever its record says. When that
   interface isn't reachable we show "Unknown" rather than guessing "Offline",
   because those mean very different things to someone chasing a fault.

   Tones follow the brand's functional palette: mint is idle-and-available,
   amber is "in use" — ringing, on a call, on hold. A dot that stays mint while
   someone's mid-call can't answer "who's free right now" at a glance, so an
   extension in use goes amber, matching the agent roster on Queues & groups. */

const STATES = {
  idle: { label: 'Online', tone: 'var(--mint)', pill: 'online' },
  on_call: { label: 'On a call', tone: 'var(--amber)', pill: 'ringing' },
  busy: { label: 'Busy', tone: 'var(--amber)', pill: 'ringing' },
  ringing: { label: 'Ringing', tone: 'var(--amber)', pill: 'ringing' },
  on_hold: { label: 'On hold', tone: 'var(--amber)', pill: 'ringing' },
  // Registered but not answering our checks — a different fault from offline,
  // and worth its own colour so it isn't mistaken for a phone that's simply
  // switched off.
  unreachable: { label: 'Not responding', tone: 'var(--amber)', pill: 'ringing' },
  offline: { label: 'Offline', tone: 'var(--muted)', pill: 'offline' },
  unknown: { label: 'Unknown', tone: 'var(--muted)', pill: 'offline' },
};

export default function ExtensionStatus({ status, device }) {
  const state = STATES[status?.state ?? 'unknown'] ?? STATES.unknown;

  return (
    <span
      title={tooltip(status, device)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'help', whiteSpace: 'nowrap' }}
    >
      <Circle size={8} fill={state.tone} color={state.tone} />
      <span style={{ fontSize: 13.5, fontWeight: 500 }}>{state.label}</span>
    </span>
  );
}

/** What a person chasing "my phone isn't ringing" actually wants to know. */
function tooltip(status, device) {
  if (!status || status.state === 'unknown') {
    return "We can't reach your phone system to check this extension right now.";
  }

  if (!status.registered) {
    return 'Not registered — the handset isn\'t currently connected to us.';
  }

  const lines = [
    status.reachable ? 'Reachable' : 'Registered, but not responding',
    status.ip ? `Connected from ${status.ip}` : null,
    status.local_ip && status.local_ip !== status.ip ? `On its network as ${status.local_ip}` : null,
    status.latency_ms != null ? `Round trip ${status.latency_ms} ms` : null,
    status.user_agent ?? device ?? null,
  ].filter(Boolean);

  return lines.join('\n');
}
